import { useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { MotionConfig, Reorder, useDragControls } from "framer-motion"

import {
  selectActiveBattles,
  selectGame,
  selectMovementsForDisplay,
  selectPlayerMetadataCells,
  selectPlayerTotalUnits,
  selectViewerId,
} from "src/store/game"
import {
  orderedByCityOrder,
  rememberCityRows,
  selectCityOrderForGame,
  selectSelectedCity,
  setCityOrder,
} from "src/store/ui"
import { selectGameConfig } from "src/store/common"
import { selectIsReviewing } from "src/store/game"
import { contentPoint, startEdgeAutoScroll } from "src/utils/dragAutoScroll"
import { createLongPress, holdTouchScroll } from "src/utils/longPressDrag"
import { crownCells } from "src/utils/crowns"
import { useIsPhone } from "src/utils/viewport"
import constants from "src/constants"
import wsClient from "src/wsClient"
import { UPGRADE_CURRENCY_ICON, upgradeGate } from "src/utils/buildingCosts"
import { playerFacingError } from "src/utils/errorMessages"
import { settlementCostFor, upgradeOrdersFor } from "src/utils/cellActions"
import { dischargeOrderedAt, foundingOrderedAt, pendingFoundingsOf, startingCitiesOf } from "src/utils/pendingOrders"
import { tunnelContents, tunnelNetworkCapacity } from "src/utils/gameModifiers"

import UnitsCanvasDetailed from "src/components/UnitsCanvasDetailed"
import UnitsCanvas from "src/components/UnitsCanvas"
import RoundOrders from "src/components/RoundOrders"
import RetiringNote from "src/components/RetiringNote"

import "./TroopsTab.css"

/* The left dock's one panel (owner's ask, 2026-09-04) — it used to be the third of three tabs.
   What the Cities tab carried and this did not was the *alarm*: which of these towns has an enemy
   march coming for it, which is on fire, and which three are the crowns you lose the game without.
   A roster of garrisons that cannot say "this one is about to be hit" is a worse Cities tab, not a
   merge, so those three marks came across with it. */
/* The tent the map draws for a camp (R16), as an inline mark for the roster (owner's S7): the same
   triangle with a doorway, in the same "mine" green, in the row's top-right corner. */
const Tent = () => (
  <svg viewBox="0 0 12 10" width="16" height="13" aria-hidden="true">
    <path d="M1 9.5 L6 0.5 L11 9.5 Z" fill="currentColor" stroke="rgba(6, 9, 8, 0.9)" strokeWidth="0.6" />
    <path d="M4.2 9.5 L6 5.5 L7.8 9.5 Z" fill="rgba(6, 9, 8, 0.85)" />
  </svg>
)

/* One row of the roster. With `byTouch` (the phone shell) the row's own drag listener is off and a
   finger lifts it by resting on it; a mouse there still drags at once. Elsewhere it is the plain
   Reorder item it always was. */
export const CityItem = ({ value, byTouch, onLift, onSettle, onDragStart, onDragEnd, children }) => {
  const controls = useDragControls()
  const [lifted, setLifted] = useState(false)
  const releaseScroll = useRef(null)
  const lift = useRef(onLift)
  lift.current = onLift
  const release = () => {
    releaseScroll.current?.()
    releaseScroll.current = null
  }
  const longPress = useMemo(
    () =>
      createLongPress({
        onStart: (pressed) => {
          release()
          releaseScroll.current = holdTouchScroll()
          setLifted(true)
          lift.current?.()
          navigator.vibrate?.(10)
          controls.start(pressed)
        },
      }),
    [controls]
  )
  useEffect(
    () => () => {
      longPress.cancel()
      release()
    },
    [longPress]
  )
  const end = () => {
    if (!releaseScroll.current) return
    release()
    setLifted(false)
    onSettle?.()
  }
  const touchProps = byTouch
    ? {
        dragListener: false,
        dragControls: controls,
        onPointerDown: (event) => (event.pointerType === "mouse" ? controls.start(event) : longPress.onPointerDown(event)),
        onPointerMove: longPress.onPointerMove,
        // A lift that never moved is no drag at all: framer sends no end, so the lift ends here.
        onPointerUp: () => {
          longPress.onPointerUp()
          end()
        },
        onPointerCancel: () => {
          longPress.onPointerCancel()
          end()
        },
      }
    : {}
  return (
    <Reorder.Item
      as="div"
      value={value}
      drag="y"
      className={byTouch ? `troops-item touch${lifted ? " lifted" : ""}` : undefined}
      data-lifted={lifted ? "true" : undefined}
      onDragStart={onDragStart}
      onDragEnd={(...args) => {
        end()
        onDragEnd(...args)
      }}
      {...touchProps}
    >
      {children}
    </Reorder.Item>
  )
}

const prefersReducedMotion = () =>
  typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches

/* Scroll `list` so the row of `cityKey` stands at its top. Measured off the two boxes rather than
   `offsetTop`, whose parent is whichever ancestor is positioned — not necessarily the scroller.

   The last rows of a roster have too little under them to reach the top, so `spacer` (an empty
   block at the list's end) is grown first by exactly what is missing: the list's height less
   everything from the row down. Without it the bottom city opened and stayed at the bottom. */
export const scrollRowToTop = (list, cityKey, spacer) => {
  const row = list?.querySelector(`[data-city="${cityKey}"]`)
  if (!row || typeof list.scrollTo !== "function") return
  // The last open row's room goes first: taking it away can pull `scrollTop` in, which moves
  // the row, so the row is measured only once it has.
  if (spacer) spacer.style.height = "0px"
  const offset = row.getBoundingClientRect().top - list.getBoundingClientRect().top
  if (spacer) {
    const below = list.scrollHeight - (list.scrollTop + offset)
    spacer.style.height = `${Math.max(0, list.clientHeight - below)}px`
  }
  const top = list.scrollTop + offset
  list.scrollTo({ top: Math.max(0, top), behavior: prefersReducedMotion() ? "auto" : "smooth" })
}

// How long after a press on a roster row a selection of that row still counts as the press's own.
const ROSTER_PRESS_MS = 1000

const TroopsTab = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const currentUnits = useSelector(selectPlayerTotalUnits)
  const game = useSelector(selectGame)
  const userId = useSelector(selectViewerId)
  const gameConfig = useSelector(selectGameConfig)
  const order = useSelector((state) => selectCityOrderForGame(state, game?.id))
  const playerCellsMetadata = useSelector(selectPlayerMetadataCells)
  const movements = useSelector(selectMovementsForDisplay)
  const battles = useSelector(selectActiveBattles)
  const team = game?.metadata?.users?.[String(userId)]?.team
  const cells = game?.metadata?.cells || {}

  /* The three buildings a city grows on, upgradable from the row (owner, 2026-09-09: "so we don't
     need to go over one per one"). Deliberately three and not five: these are the ones a player
     raises every round — bodies, food, research — and the wall and the special building are
     decisions rather than upkeep. `wall` in particular would be a fourth chip that is right to
     press at one city in ten.

     Named per side rather than inferred, because "the growth buildings" is a design fact and not
     a property of the config. */
  /* The city's own level — command centre or hatchery — which is what a player means by "how big
     is this city". It sits under the coordinates rather than in the rank of buttons (owner,
     2026-09-10), because it reads as a property of the place. */
  const MAIN_BUILDING = {
    human: constants.buildings.human.COMMAND_CENTER,
    zombie: constants.buildings.zombie.HATCHERY,
  }

  /* The other two, as the two tiles on the right. */
  const UPGRADABLE = {
    human: [constants.buildings.human.CULTIVATION_CENTER, constants.buildings.human.ACADEMY],
    zombie: [constants.buildings.zombie.PUTREFACTOR, constants.buildings.zombie.EVOLUTION_CHAMBER],
  }
  // Units always in the config's order — worker, soldier, ranger, … (owner's S8): a row used to
  // list them in whatever order they had arrived at the cell, so no two cities read alike.
  const unitOrder = Object.keys(gameConfig?.[team === "zombie" ? "zombie_config" : "human_config"]?.TROOPS || {})
  const rank = (type) => {
    const index = unitOrder.indexOf(type)
    return index === -1 ? unitOrder.length : index
  }
  const inConfigOrder = (units) => [...units].sort((a, b) => rank(a.type) - rank(b.type))

  // The cells burning right now, so "is any of mine on fire?" is a glance rather than a hunt.
  const burning = new Set(battles.map((battle) => `${battle.col}-${battle.row}`))
  // Which three of these are this player's crowns — the cities their elimination is counted on.
  const crowns = crownCells(game?.metadata, game?.players)
  const citiesUnderAttack = new Set(
    movements
      // Hostile only: an ally sending reinforcements is not an attack.
      .filter((movement) => movement.relation === "enemy")
      // Every cell the march still has to cross, not just its destination (owner's Q15): a march
      // stops and fights at the first garrisoned cell on its route, so a city on the path is
      // exactly as threatened as the one at the end of it.
      .flatMap((movement) =>
        (movement.remainingPath?.length ? movement.remainingPath : [movement.destination]).map(
          (cell) => `${cell.column}-${cell.row}`
        )
      )
  )
  const troopsDistribution = Object.entries(playerCellsMetadata)
    // Every city, garrisoned or not (owner's Q6): an empty city is exactly the row a player
    // reinforces from this tab, so hiding it hid the problem. Ground merely stood on still needs
    // units to earn a row; lost cities where only inert buildings remain get none.
    .filter(([coordinates, playerCellData]) => {
      const garrisoned = Object.values(playerCellData.units).some((unit) => unit.number > 0)
      // A colony ordered here keeps its row (owner, 2026-10-04): the founders are held by the
      // order, so the free workers can read zero while the party still stands on the cell.
      const [col, row] = coordinates.split("-").map(Number)
      const founding = foundingOrderedAt({ metadata: game?.metadata, col, row })
      const founder = founding != null && String(founding.user_id) === String(userId)
      // And a camp whose every body is retiring keeps its row (owner, 2026-10-23): the bodies are
      // held by the order, so the free garrison reads zero while they still stand on the cell.
      const retiring = dischargeOrderedAt({ metadata: game?.metadata, userId, col, row }) != null
      return garrisoned || founder || retiring || cells[coordinates]?.owner === userId
    })
    .map(([coordinates, playerCellData]) => {
      return {
        city: {
          col: parseInt(coordinates.split("-")[0], 10),
          row: parseInt(coordinates.split("-")[1], 10),
        },
        held: cells[coordinates]?.owner === userId,
        underAttack: citiesUnderAttack.has(coordinates),
        burning: burning.has(coordinates),
        isCrown: crowns[coordinates]?.userId === String(userId),
        workersHere: playerCellData.units?.[constants.units.human.WORKER]?.number ?? 0,
        units: inConfigOrder(
          Object.entries(playerCellData.units)
            .filter(([, unitData]) => unitData.number > 0)
            .map(([unitType, unitData]) => ({
              type: unitType,
              number: unitData.number,
            }))
        ),
      }
    })

  const orderedIds = orderedByCityOrder(
    troopsDistribution.map((row) => `${row.city.col}-${row.city.row}`),
    order
  )
  const byId = Object.fromEntries(troopsDistribution.map((row) => [`${row.city.col}-${row.city.row}`, row]))
  const orderedRows = orderedIds.map((id) => byId[id])

  /* Whether these rows have hands at all, and whether those hands may be used right now — two
     different questions, and they were one until the owner said so (2026-09-10: "marking as ready
     should disable the buttons, not make them disappear").

     A **replay** has no hands: there is nothing to press and nothing that could be pressed, so the
     controls are not drawn, which is what every panel below the map does in review.

     A player who has **readied up** does. The server refuses the press (`UserAlreadyReady`) and
     un-readying is one button away, so the right answer is a disabled control that says why — not
     a row that changes shape the moment the round is locked in, which is both a flinch and a loss
     of the one thing those tiles are also good for: reading what the next level costs while
     waiting for everybody else.

     Upgrading costs no action, which is why nothing here reads the action budget. */
  const reviewing = useSelector(selectIsReviewing)

  /* A row the order has no rank for is appended to it, in the order it first appeared here
     (requirement 36, R1), so a camp a march just made is the last row and stays put when the
     next one lands. Written to the store rather than sorted on the fly, because "last" has to
     survive the next render and the next reload. Not in a replay: the rows there are a past
     board's, or a borrowed seat's, and the order is the player's own. */
  const unrankedKey = orderedIds.filter((id) => !order.includes(id)).join("|")
  useEffect(() => {
    if (!unrankedKey || reviewing || game?.id == null) return
    dispatch(rememberCityRows({ gameId: game.id, ids: unrankedKey.split("|") }))
  }, [unrankedKey, reviewing, game?.id, dispatch])

  const isReady = Boolean(game?.players?.find((seat) => seat.user.id === userId)?.ready)
  const readyReason = isReady ? playerFacingError("UserAlreadyReady") : null
  const race = game?.metadata?.users?.[String(userId)]?.race
  const food = game?.metadata?.users?.[String(userId)]?.food ?? 0

  /* The tunnel network (owner, 2026-09-30: "The UI in the left side has to say X / limit. And also
     display the units as in the TOTAL UNITS"): what is underground against what the tunnel
     program lets it hold, for a horde player who has the program or anything in the network. */
  const me = game?.metadata?.users?.[String(userId)]
  const tunnelUnits = me?.tunnel || {}
  const inTunnel = tunnelContents(tunnelUnits)
  const tunnelLimit = tunnelNetworkCapacity({ gameConfig, researchLevels: me?.researches })
  const tunnelRow =
    team === "zombie" && !reviewing && (tunnelLimit > 0 || inTunnel > 0)
      ? {
          count: inTunnel,
          limit: tunnelLimit,
          units: Object.entries(tunnelUnits).map(([type, unit]) => ({ type, number: unit?.number ?? 0 })),
        }
      : null

  /* The same gates `level_up_building` checks, answered before the press instead of after it —
     the rule `utils/buildingCosts` exists for, and the same call the city sheet's price arrow
     makes. Humanity pays in the workers standing on that very cell, so the purse is per row; the
     horde pays from one meat pool, so it is not. */
  const sectionFor = (row, code) => {
    const coordinates = `${row.city.col}-${row.city.row}`
    const workersOnCell = row.units.find((unit) => unit.type === constants.units.human.WORKER)?.number ?? 0
    const level = cells[coordinates]?.metadata_by_user?.[String(userId)]?.buildings?.[code]?.level ?? 0
    // One level a building a round, landing when it turns (owner, 2026-10-02).
    const orders = upgradeOrdersFor({ game, gameConfig, userId, team, race, col: row.city.col, row: row.city.row, buildingCode: code })
    const gate = upgradeGate({
      gameConfig,
      team,
      buildingCode: code,
      level,
      race,
      workersOnCell,
      food,
      ...orders,
    })
    return {
      code,
      level,
      // ⛏️ on the tile while the level is on its way.
      upgrading: orders.ordered,
      cost: gate.cost,
      currency: UPGRADE_CURRENCY_ICON[team] ?? "",
      // Readiness first: it is the refusal the player can lift, and the one they are looking at.
      allowed: gate.allowed && !isReady,
      reason: readyReason || (gate.reason ? t(`city.upgradeBlocked.${gate.reason}`) : null),
    }
  }

  const mainBuildingFor = (row) => {
    const code = MAIN_BUILDING[team]
    if (!code || reviewing || !row.held) return null
    return sectionFor(row, code)
  }

  const buildingsFor = (row) => {
    const codes = UPGRADABLE[team]
    if (!codes || reviewing || !row.held) return null
    return codes.map((code) => sectionFor(row, code))
  }

  const upgrade = (city) => (code) => {
    wsClient.send("game", { action: "level_up_building", column: city.col, row: city.row, building: code })
  }
  // A tile with a level on its way takes the order back when pressed (owner, 2026-10-03).
  const cancelUpgrade = (city) => (code) => {
    wsClient.send("game", { action: "cancel_upgrade", column: city.col, row: city.row, building: code })
  }

  /* The one thing a camp's row can do: found a city where the troops are standing (owner,
     2026-09-09). Same price and same gates the city sheet's footer applies — humanity pays in the
     workers standing on that very cell, the horde in meat from the pool, and neither may found
     while the ground is still being fought over (`CannotSettleWhileFighting`).

     The rule is `found_settlement`'s, not this panel's: what is answered here is only whether the
     server would say yes, so a button that will be refused is disabled with the reason on it. */
  const settleFor = (row) => {
    if (row.held || reviewing || !team) return null
    const initialLocations = startingCitiesOf(game?.metadata?.users?.[String(userId)])
    const pending = pendingFoundingsOf({ metadata: game?.metadata, userId })
    const rules = gameConfig?.rules_config
    const cost = settlementCostFor({ rules, cells, userId, team, initialLocations, pending })
    // A colony is founded when the round turns (owner, 2026-10-02): one a cell. The bank and the
    // workers here are already the free ones (`pendingOrders.effectiveForPlayer`).
    const ordered = foundingOrderedAt({ metadata: game?.metadata, col: row.city.col, row: row.city.row })
    const canPay = team === "human" ? row.workersHere >= cost : food >= cost
    const reason =
      readyReason ||
      (row.burning
        ? t("game.mapAction.reason.siegeNoSettle")
        : ordered
          ? t("game.mapAction.reason.settleOrdered")
          : canPay
          ? null
          : team === "human"
            ? t("game.mapAction.reason.needFoundingWorkers", { cost, workers: row.workersHere })
            : t("game.mapAction.reason.needFood", { cost, food }))
    return {
      cost,
      currency: UPGRADE_CURRENCY_ICON[team] ?? "",
      // The picture of what founding makes: the command centre, or the hatchery (owner,
      // 2026-09-10). A deed's tile had no art of its own — there is no building there yet — and a
      // flag on an empty tile said less than the thing the press would put on the ground.
      code: MAIN_BUILDING[team] ?? null,
      // Ordered here by ME: the tile is the building tiles' "on its way" and a press cancels it.
      // An ally's order on the same camp greys SETTLE with the reason instead — the server would
      // refuse the cancel (`NoFoundingToCancel`), and the pick would promise a press that does nothing.
      founding: Boolean(ordered) && String(ordered.user_id) === String(userId),
      allowed: !reason,
      reason,
    }
  }

  const settle = (city) => () => {
    wsClient.send("game", { action: "found_settlement", column: city.col, row: city.row })
  }
  // A base on its way takes the order back when pressed (owner, 2026-10-03), like a building's level.
  const cancelSettle = (city) => () => {
    wsClient.send("game", { action: "cancel_founding", column: city.col, row: city.row })
  }

  // A completed drag must not also fire the row's focus-the-map click.
  const draggingUntil = useRef(0)
  const listRef = useRef(null)
  /* A drag near the list's top or bottom edge scrolls it, and the row stays under the pointer while
     it does (owner, 2026-09-29; `utils/dragAutoScroll` has the three pieces). */
  const dragPoint = useMemo(() => contentPoint(listRef), [])
  const stopAutoScroll = useRef(null)
  const endAutoScroll = () => {
    stopAutoScroll.current?.()
    stopAutoScroll.current = null
  }
  useEffect(() => endAutoScroll, [])
  /* On a phone a row is lifted by resting a finger on it (owner, 2026-10-02: "Let's do the drag
     and drop for the cities for the mobile version pls"), and a finger that moves scrolls the list.

     It had no dragging at all since 2026-09-09 ("forget the city reordering while drag and drop,
     because we cannot scroll then"): framer-motion takes the pointer as soon as the finger moves,
     so the roster could not be scrolled past its first screen. Time is what tells the two apart,
     the way every phone's own lists do — `utils/longPressDrag` has the rest. The rows, the marks
     and the click-to-fly stay the one component on both shells. */
  const isPhone = useIsPhone()

  /* The city open on the map — its sheet, or the action bar under a selected cell, which are the
     same `selectedCity` (owner, 2026-10-07: "a visual effect on the left side bar, so we know which
     city is open + include scrolling so the city is in the top of the bar"). Lit while it is open,
     and scrolled to the top once each time a different one opens: a roster that re-scrolled on
     every render would take the list from a player scrolling it. Not mid-drag either.

     And never for a press on the roster itself (owner, the same day: "this scroll-to-top is
     annoying when pressing the side bar ... just do that when it's via the map"). The row is
     already where the player is looking, and moving it put a different row under the second press.
     `pressedHere` is the row pressed and when; a selection of that row inside `ROSTER_PRESS_MS`
     is the press's own, and anything else — the map, the C key, a coordinate in the report —
     scrolls. The time bound keeps a press that selected nothing (a tile) from muting a later
     selection of the same city off the map. */
  const selected = useSelector(selectSelectedCity)
  const openKey = selected ? `${selected.col}-${selected.row}` : null
  const openIsListed = openKey != null && orderedIds.includes(openKey)
  const spacerRef = useRef(null)
  const pressedHere = useRef({ key: null, at: 0 })
  useEffect(() => {
    if (!openIsListed) {
      // Nothing open: the room made for the last row goes, and the list keeps its own length.
      if (spacerRef.current) spacerRef.current.style.height = "0px"
      return
    }
    const pressed = pressedHere.current
    pressedHere.current = { key: null, at: 0 }
    if (pressed.key === openKey && Date.now() - pressed.at < ROSTER_PRESS_MS) return
    if (draggingUntil.current === Infinity) return
    scrollRowToTop(listRef.current, openKey, spacerRef.current)
  }, [openKey, openIsListed])

  return (
    <div className="troops-tab">
      {/* Current Units */}
      <div className="section-header">
        <span className="p-small">{t("game.troops.totalUnits")}</span>
        <span>{currentUnits.reduce((total, u) => total + (u.number || 0), 0)}</span>
      </div>
      <UnitsCanvas unitsInfo={inConfigOrder(currentUnits)} />

      {/* The tunnel network (owner, 2026-09-29: "we have to be able to see somewhere how many
          units we have in the tunnel"; 2026-09-30: "X / limit", and the units as in the total).
          The TOTAL UNITS block's own header and strip, so the two cannot drift apart. A count and
          not a door: the exit is always a city, so SALIR lives on the tunnel city's own map order
          and city sheet. */}
      {tunnelRow && (
        <>
          <div className="section-header troops-tunnel" data-testid="troops-tunnel">
            <span className="p-small">
            <span className="troops-tunnel-glyph" aria-hidden="true">
              🕳
            </span>{" "}
            {t("game.tunnel.inTunnel")}
          </span>
            <span>
              {tunnelRow.count} / {tunnelRow.limit}
            </span>
          </div>
          <UnitsCanvas unitsInfo={inConfigOrder(tunnelRow.units)} />
        </>
      )}

      {/* What this round will do when it turns, and the ✕ that takes each back (owner, 2026-10-05). */}
      <RoundOrders />

      {/* Troops by city (Scrollable) */}
      <div className="section-header distribution-section">
        <span className="p-small">{t("game.troops.distribution")}</span>
        <span>{orderedRows.length}</span>
      </div>
      {/* Rows drag into place and write the same order every tab keeps (owner's Q6 / P5). */}
      <MotionConfig transformPagePoint={dragPoint}>
        <Reorder.Group
          as="div"
          axis="y"
          layoutScroll
          ref={listRef}
          className="scroll-area with-scroll-bar vertical"
          values={orderedIds}
          onReorder={(next) => dispatch(setCityOrder({ gameId: game?.id, order: next }))}
        >
          {orderedRows.length === 0 && <p className="p-small troops-empty">{t("game.troops.empty")}</p>}
          {orderedRows.map((row) => (
            <CityItem
              key={`${row.city.col}-${row.city.row}`}
              value={`${row.city.col}-${row.city.row}`}
              byTouch={isPhone}
              /* A row lifted and put down where it was is no tap on the city either. */
              onLift={() => {
                draggingUntil.current = Infinity
              }}
              onSettle={() => {
                draggingUntil.current = Date.now() + 200
              }}
              onDragStart={(event) => {
                draggingUntil.current = Infinity
                endAutoScroll()
                stopAutoScroll.current = startEdgeAutoScroll(listRef.current, event)
              }}
              onDragEnd={() => {
                draggingUntil.current = Date.now() + 200
                endAutoScroll()
              }}
            >
              {/* Two marks of this panel's own around the row (owner's S7/S9): the status light —
                  red for a city on an enemy march's route or in battle, green otherwise — and the
                  tent for a camp, a cell your troops stand on that is not yours yet. */}
              <div
                className={`troops-row${row.held ? "" : " camp"}${openKey === `${row.city.col}-${row.city.row}` ? " open" : ""}`}
                data-city={`${row.city.col}-${row.city.row}`}
                onClickCapture={() => {
                  pressedHere.current = { key: `${row.city.col}-${row.city.row}`, at: Date.now() }
                }}
              >
                <span
                  className={`troops-light${row.underAttack || row.burning ? " danger" : ""}`}
                  title={row.burning ? t("game.troops.battleHere") : row.underAttack ? t("game.troops.underAttack") : undefined}
                  aria-hidden="true"
                />
                {!row.held && (
                  <span className="troops-camp" title={t("game.troops.camp")} aria-label={t("game.troops.camp")}>
                    <Tent />
                  </span>
                )}
                <UnitsCanvasDetailed
                  city={row.city}
                  held={row.held}
                  units={row.units}
                  underAttack={row.underAttack}
                  burning={row.burning}
                  isCrown={row.isCrown}
                  suppressClickUntil={draggingUntil}
                  /* A city carries its growth buildings; ground we merely stand on carries the deed
                     that would make it one. Never both — they are the same column. */
                  mainBuilding={mainBuildingFor(row)}
                  buildings={buildingsFor(row)}
                  onUpgrade={upgrade(row.city)}
                  onCancelUpgrade={cancelUpgrade(row.city)}
                  settle={settleFor(row)}
                  onSettle={settle(row.city)}
                  onCancelSettle={cancelSettle(row.city)}
                />
                {/* What retires here when the round turns, with its ✕ (owner, 2026-10-23). */}
                {!reviewing && <RetiringNote city={row.city} />}
              </div>
            </CityItem>
          ))}
          <div ref={spacerRef} className="troops-open-spacer" aria-hidden="true" />
        </Reorder.Group>
      </MotionConfig>
    </div>
  )
}

export default TroopsTab
